import config from '../config';

/**
 * Check if user has permissions for a specific module and action
 * @param {string} module - The module name (e.g., 'product', 'quotation', 'order', 'wallet')
 * @param {string} action - The action name (e.g., 'list', 'create', 'edit', 'status', 'view', 'update', 'cash-wallet', 'credit-wallet')
 * @returns {boolean} - Returns true if user has permission, false otherwise
 */
export const hasPermission = (module, action) => {
  const user = config.user;

  // If user is not authenticated, return false
  if (!user) {
    return false;
  }

  // Check if user is staff member (handle different data types)
  const isStaff = user.is_staff === 1 || user.is_staff === '1' || user.is_staff === true;

  // Handle staff members (both customers and sellers) with explicit permissions
  if (isStaff && user.permission && Array.isArray(user.permission) && user.permission.length > 0) {
    const permissions = user.permission[0]; // Take first permission object

    // Handle wallet permissions specially
    if (module === 'wallet') {
      return permissions.wallet && permissions.wallet[action] === true;
    }

    // Handle other module permissions
    if (permissions[module] && typeof permissions[module][action] !== 'undefined') {
      return permissions[module][action] === true;
    }
    
    // If permission is not defined, explicitly return false
    return false;
  }

  // Handle non-staff customer permissions
  if (user.type === 'customer' && !isStaff) {
    const customerAllowedModules = {
      'quotation': ['list', 'view', 'create'],
      'order': ['list', 'view'],
      'wallet': ['cash-wallet', 'credit-wallet', 'view']
    };

    return customerAllowedModules[module] && customerAllowedModules[module].includes(action);
  }

  // Handle non-staff sellers (regular sellers have all permissions)
  if (user.type === 'seller' && !isStaff) {
    return true;
  }

  // Default: no permission for other user types
  return false;
};

/**
 * Check if user is a staff member
 * @returns {boolean} - Returns true if user is staff member (is_staff === 1)
 */
export const isStaffMember = () => {
  const user = config.user;
  const isStaff = user && (user.is_staff === 1 || user.is_staff === '1' || user.is_staff === true);
  return user && user.type === 'seller' && isStaff;
};

/**
 * Check if user is a customer
 * @returns {boolean} - Returns true if user is a customer
 */
export const isCustomer = () => {
  const user = config.user;
  return user && user.type === 'customer';
};

/**
 * Check if user is a seller (staff or regular)
 * @returns {boolean} - Returns true if user is a seller
 */
export const isSeller = () => {
  const user = config.user;
  return user && user.type === 'seller';
};

/**
 * Get current user type
 * @returns {string|null} - Returns user type ('seller', 'customer') or null if no user
 */
export const getUserType = () => {
  const user = config.user;
  return user ? user.type : null;
};

/**
 * Get all permissions for current user
 * @returns {object|null} - Returns permission object or null if no permissions
 */
export const getUserPermissions = () => {
  const user = config.user;

  if (!user) {
    return null;
  }

  // Check if user is staff member (handle different data types)
  const isStaff = user.is_staff === 1 || user.is_staff === '1' || user.is_staff === true;

  // For staff members (customers or sellers), return explicit permissions
  if (isStaff && user.permission && user.permission.length > 0) {
    return user.permission[0];
  }

  // For non-staff customers, return predefined permissions
  if (user.type === 'customer' && !isStaff) {
    return {
      quotation: {
        list: true,
        view: true,
        create: true
      },
      order: {
        list: true,
        view: true
      },
      wallet: {
        'cash-wallet': true,
        'credit-wallet': true,
        view: true
      }
    };
  }

  // For non-staff sellers, they have all permissions
  if (user.type === 'seller' && !isStaff) {
    return 'all'; // Indicate full permissions
  }

  return null;
};

/**
 * Check if user has wallet access (either cash or credit wallet)
 * @returns {boolean} - Returns true if user has any wallet permission
 */
export const hasWalletAccess = () => {
  const user = config.user;

  if (!user) {
    return false;
  }

  // Check if user is staff member (handle different data types)
  const isStaff = user.is_staff === 1 || user.is_staff === '1' || user.is_staff === true;

  // If user is staff (regardless of type), check explicit permissions
  if (isStaff && user.permission && Array.isArray(user.permission) && user.permission.length > 0) {
    const hasCashWallet = hasPermission('wallet', 'cash-wallet');
    const hasCreditWallet = hasPermission('wallet', 'credit-wallet');
    return hasCashWallet || hasCreditWallet;
  }

  // Non-staff customers have wallet access by default
  if (user.type === 'customer' && !isStaff) {
    return true;
  }

  // Non-staff sellers have full access
  if (user.type === 'seller' && !isStaff) {
    return true;
  }

  return false;
};

/**
 * Debug function to check user's current permission status
 * @returns {object} - Returns detailed permission information for debugging
 */
export const debugUserPermissions = () => {
  const user = config.user;
  
  return {
    user: user ? {
      type: user.type,
      is_staff: user.is_staff,
      hasPermissionArray: !!(user.permission && Array.isArray(user.permission) && user.permission.length > 0),
      permissions: user.permission && user.permission.length > 0 ? user.permission[0] : null
    } : null,
    walletAccess: {
      hasWalletAccess: hasWalletAccess(),
      hasCashWallet: hasPermission('wallet', 'cash-wallet'),
      hasCreditWallet: hasPermission('wallet', 'credit-wallet'),
      canAccessWalletRoute: canAccessRoute('wallet')
    }
  };
};

/**
 * Check if user can access a specific route
 * @param {string} route - The route to check access for
 * @returns {boolean} - Returns true if user can access the route
 */
export const canAccessRoute = (route) => {
  const user = config.user;

  // If no user, return false
  if (!user) {
    return false;
  }

  // Check if user is staff member (handle different data types)
  const isStaff = user.is_staff === 1 || user.is_staff === '1' || user.is_staff === true;

  // Handle staff members (both customers and sellers) - check specific permissions
  if (isStaff) {
    // Staff member - check specific route permissions
    switch (route) {
      case 'commodity-products':
        return hasPermission('product', 'list');
      case 'quotation-list':
        return hasPermission('quotation', 'list');
      case 'order-book':
        return hasPermission('order', 'list');
      case 'wallet':
        return hasWalletAccess();
      default:
        return true; // Allow access to other routes by default
    }
  }

  // Handle non-staff customer access - customers can access certain routes
  if (user.type === 'customer' && !isStaff) {
    const customerAllowedRoutes = ['quotation-list', 'order-book', 'wallet'];
    return customerAllowedRoutes.includes(route);
  }

  // Handle non-staff sellers - they have access to all routes
  if (user.type === 'seller' && !isStaff) {
    return true;
  }

  // Default: no access for other user types or undefined cases
  return false;
};